import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getTimeLeft(target) {
  const now = new Date();
  const end = new Date(target);
  const diff = end - now;
  if (diff <= 0) return null;
  return {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((diff / (1000 * 60)) % 60),
    seconds: Math.floor((diff / 1000) % 60),
  };
}

function Digit({ value, label }) {
  return (
    <div className="flex flex-col items-center">
      <div className="relative w-16 h-16 sm:w-20 sm:h-20 rounded-lg border border-[#D4AF37]/25 bg-black/60 flex items-center justify-center overflow-hidden">
        <div className="absolute inset-x-0 top-0 h-1/2 bg-white/[0.03]" />
        <span className="font-playfair text-[#D4AF37] text-2xl sm:text-3xl font-bold relative z-10 tabular-nums">
          {String(value).padStart(2, "0")}
        </span>
      </div>
      <span className="font-montserrat text-white/30 text-[10px] tracking-[0.25em] uppercase mt-2">
        {label}
      </span>
    </div>
  );
}

export default function CountdownBanner() {
  const [config, setConfig] = useState(null);
  const [time, setTime] = useState(null);

  useEffect(() => {
    axios.get(`${API}/countdown`).then(({ data }) => {
      setConfig(data);
      setTime(getTimeLeft(data.date));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!config) return;
    const id = setInterval(() => {
      setTime(getTimeLeft(config.date));
    }, 1000);
    return () => clearInterval(id);
  }, [config]);

  if (!config || !time) return null;

  return (
    <section
      data-testid="countdown-section"
      className="relative py-14 md:py-16 overflow-hidden"
    >
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-r from-[#D4AF37]/[0.04] via-transparent to-[#FF007F]/[0.04]" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/20 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/20 to-transparent" />
      </div>

      <div className="max-w-4xl mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="flex flex-col items-center text-center"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-2 h-2 rounded-full bg-[#FF007F] animate-pulse" />
            <p
              className="font-montserrat text-[#FF007F] text-xs tracking-[0.3em] uppercase font-semibold"
              data-testid="countdown-label"
            >
              {config.title}
            </p>
          </div>

          <p className="font-montserrat text-white/40 text-xs tracking-wider mb-8">
            {config.platform}
          </p>

          <div className="flex items-center gap-3 sm:gap-5" data-testid="countdown-timer">
            <Digit value={time.days} label="Days" />
            <span className="font-playfair text-[#D4AF37]/40 text-xl sm:text-2xl mt-[-18px]">:</span>
            <Digit value={time.hours} label="Hours" />
            <span className="font-playfair text-[#D4AF37]/40 text-xl sm:text-2xl mt-[-18px]">:</span>
            <Digit value={time.minutes} label="Mins" />
            <span className="font-playfair text-[#D4AF37]/40 text-xl sm:text-2xl mt-[-18px]">:</span>
            <Digit value={time.seconds} label="Secs" />
          </div>

          <motion.a
            href="#order"
            data-testid="countdown-cta-btn"
            className="mt-8 px-8 py-3 rounded-full border border-[#D4AF37]/30 text-[#D4AF37] font-montserrat text-xs tracking-[0.2em] uppercase hover:bg-[#D4AF37]/10 hover:border-[#D4AF37]/60 transition-all duration-300"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
          >
            Reserve Your Scoop
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
}
